import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { formatDateTime } from '@/lib/utils';
import { useForm } from '@inertiajs/react';
import { Check, CircleDot, Clock, ShieldX, X } from 'lucide-react';
import { useState } from 'react';
import { route } from 'ziggy-js';

export interface SignoffStep {
    id: string;
    step: number;
    label: string;
    role: string;
    decision: 'pending' | 'approved' | 'rejected';
    is_current: boolean;
    triggers_access_revocation: boolean;
    approver: string | null;
    note: string | null;
    decided_at: string | null;
}

export interface SignoffData {
    status: 'not_required' | 'pending' | 'approved' | 'rejected';
    current_step: number | null;
    current_step_role: string | null;
    can_sign: boolean;
    subject: string | null;
    access_revocation_requested_at: string | null;
    steps: SignoffStep[];
}

const statusBadgeClass: Record<string, string> = {
    pending: 'border-border bg-warning-subtle text-warning-subtle-foreground',
    approved: 'border-border bg-success-subtle text-success-subtle-foreground',
    rejected: 'border-border bg-danger-subtle text-danger-subtle-foreground',
    not_required: 'border-border bg-muted text-muted-foreground',
};

function DecisionIcon({ decision, isCurrent }: { decision: string; isCurrent: boolean }) {
    if (decision === 'approved') return <Check className="text-success-subtle-foreground h-4 w-4" />;
    if (decision === 'rejected') return <X className="text-danger-subtle-foreground h-4 w-4" />;
    if (isCurrent) return <CircleDot className="text-warning-subtle-foreground h-4 w-4" />;
    return <Clock className="text-muted-foreground h-4 w-4" />;
}

export function SignoffPanel({ requestId, approval }: { requestId: string; approval: SignoffData }) {
    const { t } = useTranslations();
    const [rejecting, setRejecting] = useState(false);
    const { data, setData, post, processing, reset } = useForm<{ note: string }>({ note: '' });

    const sign = () => {
        post(route('service-requests.sign', requestId), { preserveScroll: true, onSuccess: () => reset() });
    };

    const reject = () => {
        post(route('service-requests.reject', requestId), {
            preserveScroll: true,
            onSuccess: () => {
                reset();
                setRejecting(false);
            },
        });
    };

    return (
        <div className="bg-card rounded-xl border p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold tracking-wide uppercase">{t('service_requests.signoff.title', 'Sign-off')}</h2>
                <Badge variant="outline" className={statusBadgeClass[approval.status] ?? ''}>
                    {t(`service_requests.signoff.status_${approval.status}`, approval.status)}
                </Badge>
                {approval.access_revocation_requested_at && (
                    <Badge variant="outline" className="border-border bg-danger-subtle text-danger-subtle-foreground">
                        <ShieldX className="mr-1 h-3 w-3" />
                        {t('service_requests.signoff.revocation_flagged', 'Access revocation requested')}
                    </Badge>
                )}
            </div>

            {approval.subject && (
                <p className="text-muted-foreground mb-4 text-sm">
                    {t('service_requests.signoff.subject', 'Employee')}: <span className="text-foreground font-medium">{approval.subject}</span>
                </p>
            )}

            <ol className="space-y-2.5">
                {approval.steps.map((step) => (
                    <li
                        key={step.id}
                        className={`flex items-start gap-3 rounded-lg border p-3 ${step.is_current ? 'border-border bg-warning-subtle' : ''}`}
                    >
                        <div className="mt-0.5">
                            <DecisionIcon decision={step.decision} isCurrent={step.is_current} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium">
                                    {step.step}. {step.label}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                    {step.role}
                                </Badge>
                                {step.triggers_access_revocation && (
                                    <Badge variant="outline" className="border-border bg-danger-subtle text-danger-subtle-foreground text-xs">
                                        {t('service_requests.signoff.it_step', 'IT')}
                                    </Badge>
                                )}
                            </div>
                            {step.approver && (
                                <p className="text-muted-foreground mt-1 text-xs">
                                    {step.decision === 'rejected'
                                        ? t('service_requests.signoff.rejected_by', 'Rejected by')
                                        : t('service_requests.signoff.signed_by', 'Signed by')}{' '}
                                    {step.approver}
                                    {step.decided_at ? ` · ${formatDateTime(step.decided_at)}` : ''}
                                </p>
                            )}
                            {step.note && <p className="mt-1 text-sm whitespace-pre-wrap">{step.note}</p>}
                        </div>
                    </li>
                ))}
            </ol>

            {approval.can_sign && approval.status === 'pending' && (
                <div className="mt-4 space-y-3 border-t pt-4">
                    <Textarea
                        value={data.note}
                        onChange={(e) => setData('note', e.target.value)}
                        rows={2}
                        placeholder={t('service_requests.signoff.note_placeholder', 'Optional note…')}
                    />
                    {rejecting ? (
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="destructive" size="sm" disabled={processing} onClick={reject}>
                                {t('service_requests.signoff.confirm_reject', 'Confirm rejection')}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => setRejecting(false)}>
                                {t('service_requests.cancel', 'Cancel')}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" size="sm" disabled={processing} onClick={sign}>
                                <Check className="mr-1 h-4 w-4" />
                                {t('service_requests.signoff.sign', 'Sign off')}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => setRejecting(true)}>
                                <X className="mr-1 h-4 w-4" />
                                {t('service_requests.signoff.reject', 'Reject')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
